import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { thermalProductsByCategory, thermalProductPath, THERMAL_COMPARE_GROUPS, getThermalCell } from '../data/thermal';

const ThermalCamerasCompareTable = () => {
  const cameras = thermalProductsByCategory('thermal-cameras');

  return (
    <section className="py-16 sm:py-20 bg-white" data-testid="thermal-compare-table-section" id="compare">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 text-center mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e2d3b] mb-3">Compare Every Thermal Camera</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-orange-500 mx-auto mb-6"></div>
          <p className="text-lg text-slate-600 leading-relaxed">
            All five thermal cameras side-by-side. Pick the resolution, focus system, and temperature range that fit your
            inspections — then jump straight to the full product page.
          </p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm" data-testid="thermal-compare-table-scroll">
          <table className="w-full min-w-[900px] border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-white w-56 min-w-[14rem] text-left align-bottom p-6 border-b border-slate-200">
                  <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">Model</span>
                </th>
                {cameras.map((cam) => (
                  <th key={cam.slug} className="align-top p-6 border-b border-l border-slate-200 bg-white min-w-[180px]" data-testid={`thermal-compare-header-${cam.slug}`}>
                    <div className="flex flex-col items-center text-center">
                      <div className="h-28 w-full flex items-center justify-center mb-3">
                        <img src={cam.images[0]} alt={`${cam.name} thermal camera`} className="max-h-28 w-auto object-contain" loading="lazy" />
                      </div>
                      <p className="text-lg font-bold text-[#1e2d3b] leading-tight mb-1">{cam.menuName}</p>
                      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{cam.models}</p>
                      <p className="text-xs text-blue-600 font-medium mb-4 leading-snug">{cam.tagline}</p>
                      <Link to={thermalProductPath(cam)} className="inline-flex items-center justify-center gap-1 text-sm font-semibold text-blue-600 hover:text-orange-500 transition-colors" data-testid={`thermal-compare-learn-more-${cam.slug}`}>
                        Learn more <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {THERMAL_COMPARE_GROUPS.map((group) => (
                <React.Fragment key={group.label}>
                  <tr>
                    <td colSpan={cameras.length + 1} className="bg-slate-50 px-6 py-3 text-[11px] uppercase tracking-widest font-bold text-slate-500 border-t border-slate-200">
                      {group.label}
                    </td>
                  </tr>
                  {group.rows.map((row, ri) => (
                    <tr key={row.key} id={`thermal-row-${row.key}`} data-testid={`thermal-compare-row-${row.key}`} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}>
                      <th scope="row" className="sticky left-0 z-10 bg-inherit text-left align-top px-6 py-4 font-medium text-slate-700 text-sm border-b border-slate-100 min-w-[14rem]">
                        {row.label}
                      </th>
                      {cameras.map((cam) => {
                        const val = getThermalCell(cam, row) || '—';
                        return (
                          <td key={cam.slug} className="align-top px-4 py-4 text-sm text-slate-700 border-b border-l border-slate-100 leading-snug" data-testid={`thermal-compare-cell-${cam.slug}-${row.key}`}>
                            <span className={val === '—' ? 'text-slate-300' : ''}>{val}</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-center text-xs text-slate-500 mt-4">
          Specifications from manufacturer datasheets. Contact ctrl.aero for current pricing, availability, and calibration options.
        </p>
      </div>
    </section>
  );
};

export default ThermalCamerasCompareTable;
